import { computed, ComputedRef, Ref, ref } from 'vue'
import { MaybeRefOrGetter, toValue } from '@vueuse/core'

import useRest from '../rest/useRest'
import { Model } from '../types'

import getConfig from '../plugin/getConfig'
import { Repository, useRepo } from 'pinia-orm'
import { CollectionResponse } from '../types/CollectionResponse'
import { StandardErrors } from '../errors/useStandardErrors'
import { ValidationErrors } from '../errors/useValidationErrors'
import { OrionFilters } from '../types/query/OrionFilters'
import { QTable } from 'quasar'
import useQTablePaginator, { UseQTablePaginator } from './useQTablePaginator'
import { OrionSearch } from '../types/query/OrionSearch'
import { OrionAggregate } from '../types/query/OrionAggregate'
import { OrionSort } from '../types/query/OrionsSort'
import makeSqliteStorer from 'services/storage/local/makeSqliteStorer'
import { KeyValueStorer } from 'services/storage/KeyValueStorer'
import arraySerializer from 'src/shared/serializers/arraySerializer'
import { useAuthState } from 'services/auth/useAuthState'
import { useNetworkStatus } from 'src/shared/composables/useNetworkStatus/useNetworkStatus'
import {
  catalogScope,
  catalogScopeMatches,
  type CatalogScope,
} from 'src/shared/services/storage/catalogScope'
import {
  persistCatalogRecords,
  retrieveCatalogRaw,
} from 'src/shared/services/storage/catalogScopePersist'

export type OnFetchCallback<ModelType extends Model> = (models: CollectionResponse<ModelType>) => void

export interface UseFetchResourcesOptions<ModelType extends Model> {
  filters?: MaybeRefOrGetter<OrionFilters>
  search?: MaybeRefOrGetter<OrionSearch>
  include?: MaybeRefOrGetter<string[]>
  sort?: MaybeRefOrGetter<OrionSort[]>
  aggregates?: MaybeRefOrGetter<OrionAggregate[]>
  onFetch?: OnFetchCallback<ModelType>
  immediate?: boolean
  notifyOnError?: boolean
  localStorerMaker?: (dbKey: string | (() => string), key: string) => KeyValueStorer
  storagePersist?: boolean
  preferStorage?: boolean
  allowStorage?: boolean
  persist?: boolean
  persistBy?: 'save' | 'replace'
  limit?: MaybeRefOrGetter<number>
  qTablePaginator?: UseQTablePaginator
  recursive?: boolean
  withoutTenant?: boolean | undefined,
  withoutOrg?: boolean | undefined,
  withoutToken?: boolean | undefined,
}

export interface UseFetcherResourcesReturn<ModelType extends Model> {
  fetch: (fetchOptions?: { page?: number }) => Promise<void>
  filters: MaybeRefOrGetter<OrionFilters | undefined>
  search: MaybeRefOrGetter<OrionSearch | undefined>
  sort: MaybeRefOrGetter<OrionSort[] | undefined>
  aggregates: MaybeRefOrGetter<OrionAggregate[] | undefined>
  meta: ComputedRef<CollectionResponse<ModelType>['meta'] | undefined>
  nextPage: () => Promise<void>
  previousPage: () => Promise<void>
  toPage: (page: number) => Promise<void>
  isFirstPage: ComputedRef<boolean | undefined>
  isLastPage: ComputedRef<boolean | undefined>
  data: Ref<CollectionResponse<ModelType> | null>
  fetching: Ref<boolean>
  resources: Ref<ModelType[]>
  retrieveRecordsFromStorer: (boundScope?: CatalogScope) => Promise<ModelType[] | undefined>
  persistRecordsToStorer: (records: ModelType[], boundScope?: CatalogScope) => Promise<void>
  hasErrors: ComputedRef<boolean>
  validationErrors: Ref<ValidationErrors>
  standardErrors: Ref<StandardErrors<string | number>>
  hasValidationErrors: ComputedRef<boolean>
  hasStandardErrors: ComputedRef<boolean>
  onFetch: (callback: OnFetchCallback<ModelType>) => void
  repo: Repository<ModelType>
  qTablePagination: Ref<UseQTablePaginator>
  onQTableRequest: QTable['onRequest']
}

const defaultOptions = {
  notifyOnError: true,
  localStorerMaker: makeSqliteStorer,
  persist: true,
  storagePersist: false,
  recursive: false,
  persistBy: 'replace',
}

function useFetchResources<ModelType extends typeof Model> (
  modelClass: ModelType,
  options: UseFetchResourcesOptions<InstanceType<ModelType>> = {},
): UseFetcherResourcesReturn<InstanceType<ModelType>> {
  options = Object.assign({}, defaultOptions, options)
  const authState = useAuthState()

  const repo = useRepo<InstanceType<ModelType>>(modelClass)

  const storageIdentifier = `entities:${modelClass.entity}.records`

  const networkStatus = useNetworkStatus()

  function liveScope (): CatalogScope | undefined {
    return catalogScope(authState.tenantId.value, authState.orgId.value)
  }

  const storersByKey = new Map<string, KeyValueStorer>()

  function storerForKey (key: string) {
    let bound = storersByKey.get(key)
    if (!bound && options.localStorerMaker) {
      bound = options.localStorerMaker(key, storageIdentifier)
      storersByKey.set(key, bound)
    }
    return bound
  }

  const skipStorageOnNextFetch = ref(false)

  const qTablePaginator = useQTablePaginator(options.qTablePaginator || {
    rowsPerPage: 1000,
    sortBy: 'name',
    descending: false,
    page: 1,
    rowsNumber: 0,
  })

  const entity = modelClass.entity

  const config = getConfig('default')
  const errorNotifier = config.errorNotifiers?.fetch

  const onFetchCallbacks = ref<OnFetchCallback<InstanceType<ModelType>>[]>([])

  if (options.onFetch) {
    onFetchCallbacks.value.push(options.onFetch)
  }

  const onFetch = (callback: OnFetchCallback<InstanceType<ModelType>>) => {
    onFetchCallbacks.value.push(callback)
  }

  const resources: Ref<InstanceType<ModelType>[]> = ref([])

  const rest = useRest<CollectionResponse<InstanceType<ModelType>>>(entity + '/search')

  async function retrieveRecordsFromStorer (boundScope?: CatalogScope) {
    const raw = await retrieveCatalogRaw({
      runScope: boundScope ?? liveScope(),
      liveScope,
      get: async (key) => storerForKey(key)?.get(),
    })
    return raw ? (arraySerializer.read(raw) as InstanceType<ModelType>[]) : undefined
  }

  async function persistRecordsToStorer (records: InstanceType<ModelType>[], boundScope?: CatalogScope) {
    const runScope = boundScope ?? liveScope()
    await persistCatalogRecords({
      runScope,
      liveScope,
      records,
      set: async (key, value, stillCurrent) => {
        if (!stillCurrent()) {
          return
        }
        await storerForKey(key)?.set(value)
      },
    })
  }

  async function fetch (fetchOptions?: { page?: number }, query: Record<string, any> = {}) {
    const runScope = liveScope()
    let fetchedFromStore = false
    if (
      (options.preferStorage && options.localStorerMaker && !skipStorageOnNextFetch.value) ||
      (options.allowStorage && options.localStorerMaker && !skipStorageOnNextFetch.value && !networkStatus.connected.value)
    ) {
      const resourcesFromStorageRaw = await retrieveCatalogRaw({
        runScope,
        liveScope,
        get: async (key) => storerForKey(key)?.get(),
      })
      if (!catalogScopeMatches(runScope, liveScope()) && runScope) {
        return
      }
      const resourcesFromStorage = resourcesFromStorageRaw ? arraySerializer.read(resourcesFromStorageRaw) : undefined
      if (resourcesFromStorageRaw && resourcesFromStorageRaw !== 'undefined' && resourcesFromStorageRaw !== 'null') {
        if (resourcesFromStorage && typeof resourcesFromStorage === 'object') {
          const resourcesFromStoreArray = Object.values(resourcesFromStorage)
          resources.value = resourcesFromStoreArray
          repo.flush()
          repo.save(resourcesFromStoreArray)
          fetchedFromStore = true
        }
      }
    }

    if (!fetchedFromStore) {
      await rest.execute(
        'post',
        {
          page: fetchOptions?.page,
          filters: toValue(options.filters),
          limit: toValue(options.limit) || qTablePaginator.pagination.value.rowsPerPage,
          sort: toValue(options.sort),
          search: toValue(options.search),
          aggregates: toValue(options.aggregates),
          ...query,
        },
        {
          include: (toValue(options.include) || []).join(',') || undefined,
        },
        {
          withoutToken: options.withoutToken,
          withoutTenant: options.withoutTenant,
          withoutOrg: options.withoutOrg,
        },
      )
      if (runScope && !catalogScopeMatches(runScope, liveScope())) {
        return
      }
    }

    if (runScope && !catalogScopeMatches(runScope, liveScope())) {
      return
    }

    if (!rest.hasErrors.value) {
      if (rest.data.value?.data) {
        resources.value = rest.data.value.data
        if (options.storagePersist) {
          persistRecordsToStorer(resources.value, runScope)
        }
      }

      if (options.persist) {
        if (options.persistBy === 'replace') {
          repo.flush()
          repo.save(resources.value)
        } else {
          repo[options.persistBy || 'save'](resources.value)
        }
      }

      onFetchCallbacks.value.forEach(callback => {
        if (rest.data.value) {
          callback(rest.data.value)
        }
      })

      if (rest.data.value?.page) {
        qTablePaginator.setPaginationFromOrionResponse(rest.data.value)
      }
    } else {
      if (errorNotifier && options.notifyOnError) errorNotifier({ entityType: entity })
    }

    if (options.recursive) {
      if (!isLastPage.value) {
        skipStorageOnNextFetch.value = true
        await nextPage()
      } else {
        skipStorageOnNextFetch.value = false
      }
    }
  }

  if (options.immediate) {
    fetch()
  }

  const meta = computed(() => rest.data.value?.meta)
  const links = computed(() => rest.data.value?.links)

  const isFirstPage = computed(() => meta.value?.current_page === 1)

  const isLastPage = computed(() => {
    return meta.value && meta.value?.current_page === meta.value.last_page
  })

  async function nextPage () {
    if (!links.value?.next) {
      console.warn('next page link not set')
      return
    }
    if (!meta.value?.current_page) {
      console.warn('missing "current_page" in meta')
      return
    }
    await fetch({ page: meta.value.current_page + 1 })
  }

  async function toPage (page: number) {
    await fetch({ page })
  }

  async function previousPage () {
    if (isFirstPage.value) {
      console.warn('Cannot fetch previous page when on the first page')
      return
    }
    if (!meta.value?.current_page) {
      console.warn('missing "current_page" in meta')
      return
    }
    await fetch({ page: meta.value.current_page - 1 })
  }

  async function onQTableRequest (request: Parameters<NonNullable<QTable['onRequest']>>[0]) {
    const query: Record<string, any> = {}
    if (request.pagination.sortBy) {
      query.sort = [{
        field: request.pagination.sortBy,
        direction: request.pagination.descending ? 'desc' : 'asc',
      }]
    }
    await fetch({ page: request.pagination.page }, query)

    qTablePaginator.pagination.value.descending = request.pagination.descending
    qTablePaginator.pagination.value.sortBy = request.pagination.sortBy
  }

  return {
    fetch,
    nextPage,
    previousPage,
    meta,
    toPage,
    filters: options.filters || ref(undefined),
    search: options.search || ref(undefined),
    aggregates: options.aggregates || ref(undefined),
    isFirstPage,
    isLastPage,
    data: rest.data,
    fetching: rest.isFetching,
    resources,
    hasErrors: rest.hasErrors,
    validationErrors: rest.validationErrors,
    standardErrors: rest.standardErrors,
    hasValidationErrors: rest.hasValidationErrors,
    hasStandardErrors: rest.hasStandardErrors,
    onFetch,
    sort: options.sort || ref(undefined),
    repo,
    retrieveRecordsFromStorer,
    persistRecordsToStorer,
    onQTableRequest,
    qTablePagination: qTablePaginator.pagination,
  }
}

export { useFetchResources }
